import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Grid, Group, Progress, Stack, Tabs, Text } from '@mantine/core';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Circle,
  CircleDot,
  GraduationCap,
  Lock,
  Play,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge, Button, Card, Skeleton } from '../components/ui';
import { PageContainer, PageHeader, Section } from '../components/ui';
import { staggerContainer, rowItem } from '../lib/motion';
import { useMyLabProgress, type LabProgressLite } from '../hooks/useLabs';
import { useCourse, useEnroll } from '../hooks/useCourses';
import { BulkEnrollDialog } from '../components/course/BulkEnrollDialog';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import type { CourseModule, CourseTopic, Difficulty } from '../api/types';
import { colors } from '../theme/tokens';

const DIFFICULTY_VARIANT: Record<Difficulty, 'success' | 'info' | 'warn' | 'danger'> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

export const Route = createFileRoute('/courses/$id/')({
  component: CourseDetail,
});

function CourseDetail() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error } = useCourse(id);
  const enroll = useEnroll();
  const role = useAuthStore(selectRole);
  const [bulkOpen, setBulkOpen] = useState(false);
  const progressMap = useMyLabProgress().data ?? {};

  if (isPending) {
    return (
      <PageContainer size="default">
        <Stack gap="lg">
          <Skeleton height={48} width="50%" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={240} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="default">
        <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Could not load course">
          {(error as Error)?.message ?? 'Not found'}
        </Alert>
      </PageContainer>
    );
  }

  const isAdmin = role === 'org_admin' || role === 'superadmin' || role === 'instructor';

  return (
    <PageContainer size="default">
      <PageHeader
        icon={BookOpen}
        title={
          <Group gap="sm" align="center">
            <span>{data.title}</span>
            <Badge variant={DIFFICULTY_VARIANT[data.difficulty]}>{data.difficulty}</Badge>
          </Group>
        }
        description="Modules and progress for this course."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Courses', to: '/courses' }, { label: 'Detail' }]}
        actions={
          <>
            {isAdmin ? (
              <Button variant="secondary" leftIcon={<Users size={14} />} onClick={() => setBulkOpen(true)}>
                Bulk enroll
              </Button>
            ) : null}
            <Button
              variant="primary"
              leftIcon={<GraduationCap size={14} />}
              loading={enroll.isPending}
              onClick={() => enroll.mutate({ courseId: data.id })}
            >
              Enroll me
            </Button>
          </>
        }
      />

      {data.description ? (
        <Text c="dimmed" size="sm" maw={720} mb="lg">
          {data.description}
        </Text>
      ) : null}

      <Section title="Overview">
        <Grid gutter="md">
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Metric label="Modules" value={String(data.moduleCount)} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Metric label="Estimated hours" value={`${data.estimatedHours}h`} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Metric label="Enrolled" value={String(data.enrolledCount)} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Metric label="Difficulty" value={data.difficulty} />
          </Grid.Col>
        </Grid>
      </Section>

      <Section title="Curriculum" description="Modules, syllabus, and learning outcomes.">
        <Tabs defaultValue="modules" variant="default">
          <Tabs.List>
            <Tabs.Tab value="modules" leftSection={<BookOpen size={14} />}>
              Modules
            </Tabs.Tab>
            <Tabs.Tab value="syllabus">Syllabus</Tabs.Tab>
            <Tabs.Tab value="outcomes">Outcomes</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="modules" pt="md">
            <motion.div variants={staggerContainer} initial="hidden" animate="show">
              <Stack gap="sm">
                {data.modules.map((mod, i) => (
                  <motion.div key={mod.id} variants={rowItem}>
                    <ModuleRow mod={mod} index={i + 1} courseId={id} progress={progressMap} />
                  </motion.div>
                ))}
              </Stack>
            </motion.div>
          </Tabs.Panel>
          <Tabs.Panel value="syllabus" pt="md">
            <Card>
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                {data.syllabus}
              </Text>
            </Card>
          </Tabs.Panel>
          <Tabs.Panel value="outcomes" pt="md">
            <Card>
              <Stack gap="xs">
                {data.outcomes.map((o, i) => (
                  <Text size="sm" key={i}>
                    <span style={{ color: colors.neon, marginRight: 8 }}>✓</span>
                    {o}
                  </Text>
                ))}
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Section>

      <BulkEnrollDialog
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        courseId={data.id}
        courseTitle={data.title}
      />
    </PageContainer>
  );
}

function ModuleRow({
  mod,
  index,
  courseId,
  progress,
}: {
  mod: CourseModule;
  index: number;
  courseId: string;
  progress: Record<string, LabProgressLite>;
}) {
  const hasTopics = mod.topics && mod.topics.length > 0;
  const num = index.toString().padStart(2, '0');

  // Legacy modules (no topics) keep the simple single-row + direct lab launch.
  if (!hasTopics) {
    return <ModuleRowSimple mod={mod} index={index} />;
  }

  const topicsWithLab = mod.topics.filter((t) => t.labId);
  const total = topicsWithLab.length;
  const done = topicsWithLab.filter((t) => progress[t.labId as string]?.completed).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="md" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
            <Text className="font-mono" fw={700} size="sm" c={colors.neon} style={{ minWidth: 28 }}>
              {num}
            </Text>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">{mod.title}</Text>
              <Group gap="md">
                <Text size="xs" c="dimmed" className="font-mono">{mod.topics.length} topics</Text>
                <Text size="xs" c="dimmed" className="font-mono">{mod.durationMins}m</Text>
              </Group>
            </Stack>
          </Group>
          {total > 0 ? (
            <Badge variant={done === total ? 'success' : done > 0 ? 'info' : 'neutral'} size="sm">
              {done}/{total} done
            </Badge>
          ) : null}
        </Group>
        {total > 0 ? <Progress value={pct} size="xs" color="teal" radius="xl" /> : null}
        <Stack gap={6}>
          {mod.topics.map((t, ti) => (
            <TopicRow
              key={t.slug}
              topic={t}
              index={ti + 1}
              courseId={courseId}
              {...(t.labId && progress[t.labId] ? { state: progress[t.labId] } : {})}
            />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function TopicRow({
  topic,
  index,
  courseId,
  state,
}: {
  topic: CourseTopic;
  index: number;
  courseId: string;
  state?: LabProgressLite;
}) {
  const navigate = useNavigate();
  const completed = state?.completed ?? false;
  const inProgress =
    !completed &&
    ((state?.percentComplete ?? 0) > 0 ||
      ['ready', 'running', 'attacking', 'provisioning', 'pending'].includes(state?.status ?? ''));
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/courses/$id/topic/$topic', params: { id: courseId, topic: topic.slug } })}
      style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}
      aria-label={`Open topic ${topic.title}`}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        style={{
          padding: '10px 12px',
          borderRadius: 10,
          border: '1px solid rgb(var(--c-bd))',
          background: 'rgb(var(--c-panel))',
        }}
      >
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <span style={{ flexShrink: 0, display: 'inline-flex' }}>
            {completed ? (
              <CheckCircle2 size={16} color="rgb(var(--c-neon))" />
            ) : inProgress ? (
              <CircleDot size={16} color="#38bdf8" />
            ) : (
              <Circle size={16} color="rgb(var(--c-txt-muted))" />
            )}
          </span>
          <Text size="sm" fw={500} c="rgb(var(--c-txt-bright))" lineClamp={1}>
            {index}. {topic.title}
          </Text>
        </Group>
        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          {completed ? (
            <Badge variant="success" size="xs">done</Badge>
          ) : inProgress ? (
            <Badge variant="info" size="xs">in progress</Badge>
          ) : null}
          <Text size="xs" c="dimmed" className="font-mono">{topic.durationMins}m</Text>
          <ChevronRight size={14} color="rgb(var(--c-txt-muted))" />
        </Group>
      </Group>
    </button>
  );
}

function ModuleRowSimple({ mod, index }: { mod: CourseModule; index: number }) {

  const navigate = useNavigate();
  return (
    <Card>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap">
          <Text
            className="font-mono"
            fw={700}
            size="sm"
            c={mod.gated ? 'dimmed' : colors.neon}
            style={{ minWidth: 28 }}
          >
            {index.toString().padStart(2, '0')}
          </Text>
          <Stack gap={2}>
            <Group gap="xs">
              <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                {mod.title}
              </Text>
              {mod.gated ? <Lock size={12} color="#71717a" /> : null}
            </Group>
            <Group gap="md">
              <Text size="xs" c="dimmed" className="font-mono">
                {mod.durationMins}m
              </Text>
              {mod.labId ? (
                <Badge variant="info" size="xs">
                  Lab
                </Badge>
              ) : null}
              {mod.scenarioSlug ? (
                <Badge variant="warn" size="xs">
                  Scenario
                </Badge>
              ) : null}
            </Group>
          </Stack>
        </Group>
        <Button
          variant="ghost"
          size="xs"
          leftIcon={<Play size={12} />}
          disabled={mod.gated || !mod.labId}
          onClick={() => {
            if (mod.labId) navigate({ to: '/labs/$id', params: { id: mod.labId } });
          }}
        >
          {mod.gated ? 'Locked' : 'Select'}
        </Button>
      </Group>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <Stack gap={6}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {label}
        </Text>
        <Text className="font-mono" fw={700} size="24px" c="rgb(var(--c-txt-bright))">
          {value}
        </Text>
      </Stack>
    </Card>
  );
}
